import type {
  DipCountTone,
  DipNodeStat,
  DipNodeVariant,
  DipPeekTarget,
  DipPipelineItem,
  DipStatusBadge,
  DipTone,
} from "~/features/dataImportProjects/types";

type NodeTarget = DipPipelineItem["to"];
type BaseNodeFields = Pick<
  DipPipelineItem,
  | "id"
  | "detailPath"
  | "peek"
  | "showHandles"
  | "isDeactivated"
  | "isDisabled"
  | "alignToGroupChildIndex"
  | "nodeVariant"
  | "title"
  | "subtitle"
  | "to"
>;

interface BaseNodeInput {
  id: string;
  title: string;
  subtitle?: string | null;
  detailPath?: string | null;
  peek?: DipPeekTarget | null;
  showHandles?: boolean;
  isDeactivated?: boolean;
  isDisabled?: boolean;
  alignToGroupChildIndex?: number;
  nodeVariant?: DipNodeVariant;
  to: NodeTarget;
}

interface AggregateNodeInput extends BaseNodeInput {
  totalCount: number;
}

interface PendingNodeInput extends AggregateNodeInput {
  pendingCount: number;
}

interface ChangeRequestsNodeInput extends BaseNodeInput {
  openCount: number;
  committedCount: number;
  rejectedCount: number;
}

const CHANGED_BADGE: DipStatusBadge = { labelKey: "status.changed", tone: "neutral" };
const READY_BADGE: DipStatusBadge = { labelKey: "status.ready", tone: "attention" };
const IMPORTED_BADGE: DipStatusBadge = { labelKey: "status.imported", tone: "attention" };

function baseNode(input: BaseNodeInput): BaseNodeFields {
  return {
    id: input.id,
    detailPath: input.detailPath ?? null,
    peek: input.peek ?? null,
    title: input.title,
    subtitle: input.subtitle ?? null,
    to: input.to,
    ...(input.showHandles !== undefined ? { showHandles: input.showHandles } : {}),
    ...(input.isDeactivated !== undefined ? { isDeactivated: input.isDeactivated } : {}),
    ...(input.isDisabled !== undefined ? { isDisabled: input.isDisabled } : {}),
    ...(input.alignToGroupChildIndex !== undefined ? { alignToGroupChildIndex: input.alignToGroupChildIndex } : {}),
    ...(input.nodeVariant !== undefined ? { nodeVariant: input.nodeVariant } : {}),
  };
}

function focusCount(
  pending: number,
  total: number,
  pendingTone: DipCountTone,
): { count: number; countTone: DipCountTone } {
  return pending > 0 ? { count: pending, countTone: pendingTone } : { count: total, countTone: "white" };
}

function allStats(total: number, nonZeroTone: DipTone = "primary"): DipNodeStat[] {
  return [{ labelKey: "stats.all", value: total, tone: total > 0 ? nonZeroTone : "neutral" }];
}

function committedStats(committed: number): DipNodeStat[] {
  return [{ labelKey: "stats.committed", value: committed, tone: committed > 0 ? "success" : "neutral" }];
}

function evidenceSetStats(total: number, uncommitted: number): DipNodeStat[] {
  return [
    { labelKey: "stats.all", value: total, tone: total > 0 ? "success" : "neutral" },
    { labelKey: "stats.uncommitted", value: uncommitted, tone: uncommitted > 0 ? "success" : "neutral" },
  ];
}

function changeRequestStats(open: number, committed: number, rejected: number, nonZeroTone: DipTone): DipNodeStat[] {
  return [
    { labelKey: "stats.open", value: open, tone: open > 0 ? nonZeroTone : "neutral" },
    { labelKey: "stats.approved", value: committed, tone: committed > 0 ? nonZeroTone : "neutral" },
    { labelKey: "stats.rejected", value: rejected, tone: rejected > 0 ? nonZeroTone : "neutral" },
  ];
}

export function makeDataSourceNode(input: AggregateNodeInput): DipPipelineItem {
  return {
    ...baseNode(input),
    count: input.totalCount,
    countTone: "white",
    squareTone: "attention",
    nodeKind: "dataSource",
    badges: [input.totalCount > 0 ? IMPORTED_BADGE : READY_BADGE],
  };
}

export function makeBatchEvidencesJobNode(input: BaseNodeInput): DipPipelineItem {
  return {
    ...baseNode(input),
    count: null,
    countTone: "neutral",
    squareTone: "neutral",
    nodeKind: "batchEvidencesJob",
    badges: [],
  };
}

export function makeEvidenceImporterNode(input: BaseNodeInput): DipPipelineItem {
  return {
    ...baseNode(input),
    count: null,
    countTone: "neutral",
    squareTone: "neutral",
    nodeKind: "evidenceImporter",
    badges: [],
  };
}

export function makeEvidenceDraftsNode(input: PendingNodeInput): DipPipelineItem {
  const countDisplay = focusCount(input.pendingCount, input.totalCount, "success");
  return {
    ...baseNode(input),
    count: countDisplay.count,
    countTone: countDisplay.countTone,
    squareTone: "success",
    nodeKind: "evidenceDrafts",
    rightStats: committedStats(input.totalCount),
    badges: [],
  };
}

export function makeEvidenceSetNode(input: PendingNodeInput & { pendingCorrections?: number }): DipPipelineItem {
  const countDisplay = focusCount(input.pendingCount, input.totalCount, "success");
  return {
    ...baseNode(input),
    count: countDisplay.count,
    countTone: countDisplay.countTone,
    squareTone: "success",
    nodeKind: "evidenceSet",
    rightStats: evidenceSetStats(input.totalCount, input.pendingCount),
    badges: [],
    ...(input.pendingCorrections !== undefined ? { pendingCorrections: input.pendingCorrections } : {}),
  };
}

export function makeBatchChangeRequestsJobNode(input: BaseNodeInput): DipPipelineItem {
  return {
    ...baseNode(input),
    count: null,
    countTone: "neutral",
    squareTone: "neutral",
    nodeKind: "batchChangeRequestsJob",
    badges: [],
  };
}

export function makeComposerNode(input: BaseNodeInput & { openCount: number }): DipPipelineItem {
  return {
    ...baseNode(input),
    count: null,
    countTone: "neutral",
    squareTone: "neutral",
    nodeKind: "composer",
    badges: input.openCount > 0 ? [CHANGED_BADGE] : [],
  };
}

export function makeChangeRequestsNode(input: ChangeRequestsNodeInput): DipPipelineItem {
  const totalCount = input.openCount + input.committedCount + input.rejectedCount;
  const countDisplay = focusCount(input.openCount, totalCount, "primary");
  return {
    ...baseNode(input),
    count: countDisplay.count,
    countTone: countDisplay.countTone,
    squareTone: "primary",
    nodeKind: "changeRequests",
    rightStats: changeRequestStats(input.openCount, input.committedCount, input.rejectedCount, "primary"),
    badges: [],
  };
}

export function makeRecordsNode(input: AggregateNodeInput): DipPipelineItem {
  return {
    ...baseNode(input),
    count: input.totalCount,
    countTone: "white",
    squareTone: "primary",
    nodeKind: "records",
    rightStats: allStats(input.totalCount, "primary"),
    badges: [],
  };
}
